import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Zap, CheckCircle, Star, ArrowRight } from 'lucide-react';
import { ProductPricing } from '@/services/discount.service';
import { formatPrice } from '@/hooks/useFeaturedPricing';

/**
 * Props for AIEssentialsBundleCard component
 */
interface AIEssentialsBundleCardProps {
  /** Pricing data from API - if not provided, shows loading state */
  pricing?: ProductPricing | null;
  /** Whether pricing is currently loading */
  loading?: boolean;
}

/**
 * AIEssentialsBundleCard Component
 *
 * A reusable card component for displaying the AI Essentials Bundle
 * with professional styling, hover effects, included courses, and call-to-action buttons.
 *
 * Pricing is dynamically fetched from the backend pricing API.
 *
 * @component
 * @example
 * ```tsx
 * const { bundlePricing, loading } = useFeaturedPricing();
 * <AIEssentialsBundleCard pricing={bundlePricing} loading={loading} />
 * ```
 */
export default function AIEssentialsBundleCard({ pricing, loading = false }: AIEssentialsBundleCardProps) {
  // Extract pricing values with fallbacks for loading state
  const displayPrice = pricing?.display_price ?? 0;
  const originalPrice = pricing?.original_price ?? 0;
  const isSaleActive = pricing?.is_sale_active ?? false;
  const savingsAmount = pricing?.savings_amount ?? 0;
  const savingsPercentage = pricing?.savings_percentage ?? 0;

  // Calculate per-course price (bundle has 2 courses)
  const perCoursePrice = displayPrice > 0 ? Math.round(displayPrice / 2) : 0;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="group relative">
        {/* Mega Glow Effect */}
        <div className="absolute -inset-2 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 rounded-3xl blur-3xl opacity-30 group-hover:opacity-50 transition-all duration-500 animate-pulse"></div>

        {/* Bundle Card */}
        <div className="relative bg-gradient-to-br from-white/[0.15] to-white/[0.05] backdrop-blur-2xl rounded-3xl border-2 border-white/30 group-hover:border-white/50 transition-all duration-500 overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-blue-600/20 to-cyan-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          <div className="relative p-12">
            {/* Top Section */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 mb-12">
              {/* Left: Title & Description */}
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-600 via-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-purple-500/50 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <Sparkles className="w-10 h-10" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="bg-gradient-to-r from-purple-600/30 via-blue-600/30 to-cyan-600/30 border border-purple-500/50 px-4 py-2 rounded-full inline-block mb-2">
                      <span className="text-sm font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                        {isSaleActive ? '⚡ ULTIMATE BUNDLE' : '⚡ BEST VALUE'}
                      </span>
                    </div>
                    <h3 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                      AI Essentials Bundle
                    </h3>
                  </div>
                </div>
                <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                  Master <span className="text-purple-400 font-semibold">Prompt Engineering</span> + <span className="text-blue-400 font-semibold">AI Tools</span> in one complete package. Everything you need to become an AI power user.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  This bundle combines our two most popular courses into the ultimate AI learning experience. Perfect for professionals, entrepreneurs, and anyone who wants to stay ahead in the AI revolution.
                </p>
              </div>

              {/* Right: Pricing */}
              <div className="lg:text-right">
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20">
                  <div className="text-sm text-gray-400 uppercase tracking-wider mb-2">Bundle Price</div>
                  {loading ? (
                    /* Loading skeleton */
                    <div className="space-y-3">
                      <div className="h-16 w-32 bg-white/10 rounded animate-pulse mx-auto lg:ml-auto lg:mr-0"></div>
                      <div className="h-8 w-40 bg-white/10 rounded animate-pulse mx-auto lg:ml-auto lg:mr-0"></div>
                      <div className="h-6 w-28 bg-white/10 rounded animate-pulse mx-auto lg:ml-auto lg:mr-0"></div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline justify-center lg:justify-end gap-3 mb-2">
                        <span className="text-6xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                          {formatPrice(displayPrice)}
                        </span>
                      </div>
                      <div className="flex items-center justify-center lg:justify-end gap-2 mb-4">
                        {isSaleActive && originalPrice > displayPrice && (
                          <>
                            <span className="text-3xl text-gray-300 line-through font-semibold decoration-2">
                              {formatPrice(originalPrice)}
                            </span>
                            <span className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 px-3 py-1 rounded-full text-sm font-bold text-green-400">
                              SAVE {formatPrice(savingsAmount)}
                            </span>
                          </>
                        )}
                      </div>
                      {perCoursePrice > 0 && (
                        <div className="text-sm text-gray-400 mb-2">
                          or just <span className="text-purple-400 font-bold">{formatPrice(perCoursePrice)} per course</span>
                        </div>
                      )}
                      {isSaleActive && savingsPercentage > 0 && (
                        <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/30 px-4 py-2 rounded-full">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-medium text-purple-300">{savingsPercentage}% Savings</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* What's Included */}
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-8 border border-white/10 mb-10">
              <h4 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <span>What's Included in This Bundle</span>
              </h4>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-semibold text-white mb-1">Prompt Engineering Mastery</div>
                      <div className="text-sm text-gray-400">7 hours • 8 modules • 12+ templates</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-semibold text-white mb-1">AI Tools Accelerator</div>
                      <div className="text-sm text-gray-400">8 hours • 20+ tools • Integration guides</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">15 hours of expert training</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">Lifetime access to both courses</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">$3,000+ in templates & resources</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">Certificate of completion for each</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/ai-essentials-bundle"
                className="flex-1 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-500 hover:via-blue-500 hover:to-cyan-500 py-5 rounded-xl font-bold text-xl flex items-center justify-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 group/btn"
              >
                <span>Get The Bundle Now</span>
                <ArrowRight className="w-6 h-6 group-hover/btn:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/ai-essentials-bundle"
                className="sm:w-48 bg-white/10 hover:bg-white/20 backdrop-blur-sm py-5 rounded-xl font-bold text-lg border border-white/20 hover:border-white/40 transition-all text-center"
              >
                View Details
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-8 mt-8 pt-8 border-t border-white/10">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Instant Access</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Lifetime Updates</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Community Access</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
